/**
 * SmtpStatusCard
 *
 * Compact status panel for the System Status page (admin only).
 * Surfaces the current SMTP configuration + today's delivery counts so
 * an admin can verify outgoing email at a glance without leaving the page.
 */
import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Mail, Server, CheckCircle2, AlertTriangle, Settings2, ExternalLink, ChevronDown, BookOpen } from "lucide-react";

const SmtpStatusCard = () => {
  const [smtp, setSmtp] = useState(null);
  const [stats, setStats] = useState({ sent: 0, failed: 0, total: 0, skipped: 0 });
  const [guideOpen, setGuideOpen] = useState(false);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        apiGet("/admin/smtp-status"),
        apiGet("/admin/email-stats"),
      ]);
      setSmtp(s);
      setStats(t);
    } catch { /* admin only */ }
  };

  useEffect(() => { load(); }, []);

  if (!smtp) return null;

  const isDev = smtp.dev_mode;
  return (
    <Card className="border bg-card" data-testid="smtp-status-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Server className="h-4 w-4 text-primary" /> SMTP status
          </CardTitle>
          <CardDescription>Outbound email configuration & today's activity</CardDescription>
        </div>
        <Button asChild variant="outline" size="sm" className="gap-2" data-testid="smtp-go-to-logs">
          <Link to="/admin/email-logs"><ExternalLink className="h-3.5 w-3.5" /> Open email logs</Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={
                smtp.configured
                  ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 gap-1"
                  : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 gap-1"
              }
              data-testid="smtp-config-badge"
            >
              {smtp.configured ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
              {smtp.configured ? "SMTP configured" : "SMTP not configured"}
            </Badge>
            <Badge variant="outline" className="gap-1">
              <Settings2 className="h-3 w-3" />
              {smtp.app_env}
            </Badge>
            {isDev && (
              <Badge variant="outline" className="bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30 gap-1">
                <Mail className="h-3 w-3" /> dev mode — emails logged only
              </Badge>
            )}
          </div>
        </div>

        {smtp.configured ? (
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="rounded-md border border-border px-3 py-2">
              <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">Server</dt>
              <dd className="font-medium" data-testid="smtp-server">{smtp.server}:{smtp.port}</dd>
            </div>
            <div className="rounded-md border border-border px-3 py-2">
              <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">From</dt>
              <dd className="font-medium truncate" data-testid="smtp-from">{smtp.from_address}</dd>
            </div>
            <div className="rounded-md border border-border px-3 py-2">
              <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">Display name</dt>
              <dd className="font-medium" data-testid="smtp-from-name">{smtp.from_name}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border px-3 py-2">
            Add your Gmail App Password to <code>backend/.env</code> and set <code>APP_ENV=production</code> to enable real delivery.
            Until then every outgoing email is captured in the email logs without leaving the server.
          </p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div className="rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sent today</div>
            <div className="text-lg font-display font-semibold text-[hsl(var(--success))]" data-testid="smtp-sent-today">{stats.sent}</div>
          </div>
          <div className={`rounded-md border px-3 py-2 ${stats.failed > 0 ? "border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5" : "border-border"}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Failed today</div>
            <div
              className={`text-lg font-display font-semibold ${stats.failed > 0 ? "text-[hsl(var(--destructive))]" : ""}`}
              data-testid="smtp-failed-today"
            >
              {stats.failed}
            </div>
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Skipped today</div>
            <div className="text-lg font-display font-semibold" data-testid="smtp-skipped-today">{stats.skipped}</div>
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total today</div>
            <div className="text-lg font-display font-semibold" data-testid="smtp-total-today">{stats.total}</div>
          </div>
        </div>

        <Collapsible open={guideOpen} onOpenChange={setGuideOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full flex items-center justify-between rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors px-3 py-2 text-xs font-medium"
              data-testid="smtp-setup-guide-toggle"
            >
              <span className="inline-flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5 text-primary" />
                How to configure (Gmail App Password setup)
              </span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${guideOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="rounded-md border bg-card px-4 py-3 mt-2 space-y-2 text-xs" data-testid="smtp-setup-guide-content">
              <div className="font-semibold text-sm">How to enable email delivery</div>
              <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground leading-relaxed">
                <li>Go to your Google Account at <a className="text-primary underline-offset-2 hover:underline" href="https://myaccount.google.com" target="_blank" rel="noreferrer">myaccount.google.com</a></li>
                <li><strong>Security</strong> → <strong>2-Step Verification</strong> (enable if not already on)</li>
                <li><strong>Security</strong> → <strong>App passwords</strong></li>
                <li>Select app: <em>Mail</em>, Select device: <em>Other</em></li>
                <li>Copy the 16-character password Google generates</li>
                <li>Open the file: <code className="rounded bg-muted px-1 py-0.5 font-mono">backend/.env</code></li>
                <li>
                  Set these values:
                  <pre className="mt-1.5 rounded-md bg-muted/60 px-3 py-2 font-mono text-[11px] leading-relaxed border border-border whitespace-pre-wrap">{`MAIL_USERNAME=your@gmail.com
MAIL_PASSWORD=paste-16-char-here
MAIL_FROM=your@gmail.com
APP_ENV=production`}</pre>
                </li>
                <li>Restart the backend server (<code className="rounded bg-muted px-1 py-0.5 font-mono">supervisorctl restart backend</code>)</li>
                <li>Click the page <strong>Refresh</strong> button to confirm the SMTP badge above turns green</li>
              </ol>
              <div className="text-[11px] text-muted-foreground pt-1.5">
                <AlertTriangle className="inline h-3 w-3 mr-1" />
                Until SMTP is configured, every outgoing email is logged (visible in <em>Email logs</em>) but not actually delivered.
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default SmtpStatusCard;
